import IPlayerData from '../Player/IPlayerData';
import { IGameState } from 'State/Game/IGameState';
import { report } from '../report';

/**
 * Check for conflicts between two data sets
 */
export function conflicts(a: IPlayerData, b: IPlayerData) {
    // Small optimization, there's no need to parse level data if currencies are different
    if (Number(a.coins) !== Number(b.coins)) return true;

    const levelsA = JSON.parse(a.stars) as number[];
    const levelsB = JSON.parse(b.stars) as number[];
    const levelProgressA = levelsA.length;
    const levelProgressB = levelsB.length;
    const starsA = levelsA.reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
    const starsB = levelsB.reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);

    return levelProgressA !== levelProgressB || starsA !== starsB;
}

export function isDataValid(data: IPlayerDataLocation) {
    return data.playerData.coins !== undefined && data.playerData.stars !== undefined;
}

/**
 * Get conflicting data sets in list
 */
export function getConflictingSets(dataSets: IPlayerDataLocation[]) {
    if (dataSetsEmpty(dataSets[0], dataSets[1])) {
        return [];
    }

    if (dataSets.length > 1) {
        if (conflicts(dataSets[0].playerData, dataSets[1].playerData)) {
            return dataSets;
        }
    }

    return [];
}

/**
 * Empty data set is return on first time login in Facebook/Firebase.
 */
export function dataSetsEmpty(dataSetA: IPlayerDataLocation, dataSetB: IPlayerDataLocation): boolean {
    let aDataToCompare: boolean =
        typeof dataSetA.playerData === 'object' &&
        'coins' in dataSetA.playerData &&
        'stars' in dataSetA.playerData &&
        (JSON.parse(dataSetA.playerData.stars) as number[]).length >= 0;
    let bDataToCompare: boolean =
        typeof dataSetB.playerData === 'object' &&
        'coins' in dataSetB.playerData &&
        'stars' in dataSetB.playerData &&
        (JSON.parse(dataSetB.playerData.stars) as number[]).length >= 0;

    return !aDataToCompare || !bDataToCompare;
}

/**
 *
 */
export enum PlayerDataLocation {
    DEVICE = 'device',
    CLOUD = 'cloud',
    FACEBOOK = 'facebook',
}

/**
 * Data location could is either (device/cloud)
 */
export interface IPlayerDataLocation {
    location: PlayerDataLocation;
    playerData: IPlayerData;
}

/**
 * Sort the data sets so the data set with the highest progress is the first item in the array.
 */
export function sortDataLocationSets(sets: IPlayerDataLocation[]): IPlayerDataLocation[] {
    return sets.sort((setA, setB) => {
        const starsA = JSON.parse(setA.playerData.stars) as number[];
        const starsB = JSON.parse(setB.playerData.stars) as number[];
        const coinsA = setA.playerData.coins;
        const coinsB = setB.playerData.coins;
        return starsB.length - starsA.length || sumArray(starsB) - sumArray(starsA) || coinsB - coinsA;
    });
}

function sumArray(array: number[]) {
    return array.reduce((a: number, b: number) => a + b, 0);
}

export function hasValidFacebookData(state: IGameState, dataSets: IPlayerDataLocation[]): boolean {
    const facebookData = dataSets.filter(data => data.location === PlayerDataLocation.FACEBOOK)[0];

    return hasValidLevelData(facebookData);
}

export function hasValidLevelData(dataSet: IPlayerDataLocation) {
    return (
        typeof dataSet === 'object' &&
        typeof dataSet.playerData === 'object' &&
        'stars' in dataSet.playerData &&
        (JSON.parse(dataSet.playerData.stars) as number[]).length >= 0
    );
}

export function getHighestLevelCompleted(playerData: IPlayerDataLocation): number {
    try {
        if (!hasValidLevelData(playerData)) throw new Error('Invalid or empty player data');
        return (JSON.parse(playerData.playerData.stars) as number[]).length;
    } catch (e) {
        report(e.message);
    }
}

export function getFacebookData(dataSets: IPlayerDataLocation[]): IPlayerDataLocation {
    return dataSets.filter(dataSets => dataSets.location === PlayerDataLocation.FACEBOOK)[0];
}
